import React, { useEffect, useMemo, useRef, useState } from 'react';
import CourseService from '../services/CourseService';

const fieldNames = {
  title: 'Título', description: 'Descrição', modules: 'Módulos', lessons: 'Aulas', tasks: 'Atividades',
  formQuestions: 'Perguntas do formulário', quiz: 'Avaliação', questions: 'Perguntas', prompt: 'Enunciado',
  options: 'Alternativas', correctOptionIndex: 'Alternativa correta (posição a partir de zero)', acceptedKeywords: 'Palavras aceitas',
  durationMinutes: 'Duração em minutos', estimatedHours: 'Carga horária', difficulty: 'Dificuldade', score: 'Pontuação',
  type: 'Tipo', certificateConfig: 'Certificado', companyName: 'Instituição', cnpj: 'CNPJ', address: 'Endereço',
  contact: 'Contato', signerName: 'Assinante', primaryColor: 'Cor principal', logoPosition: 'Posição da logo',
  bodyTextTemplate: 'Texto do certificado', coverImageUrl: 'Capa', activityNotificationEmail: 'E-mail de atividades',
  instructions: 'Instruções', passingScore: 'Nota mínima', maxScore: 'Pontuação máxima', dueDays: 'Prazo em dias',
  templateId: 'Modelo', fontFamily: 'Fonte', backgroundColor: 'Cor de fundo', accentColor: 'Cor de destaque',
  studyMaterial: 'Apostila', content: 'Conteúdo', explanation: 'Justificativa', heading: 'Título do certificado', subtitle: 'Subtítulo', signerTitle: 'Cargo do assinante',
};
const protectedField = key => key !== 'templateId' && (/^(id|.*Ids?|materials|attachments|files|uploads|requiresAccessGrant|status|createdAt|updatedAt|coverImageUrl|order)$/i.test(key) || /^(url|downloadUrl|relativePath|downloadPath|mimeType|logoUrl)$/i.test(key));
const labelPath = path => path.split('.').map(key => /^\d+$/.test(key) ? String(Number(key) + 1) : (fieldNames[key] || key)).join(' › ');

// Match the editable shapes accepted by CourseAIService; uploaded assets and
// reference/security fields are deliberately handled outside text authoring.
const questionFields = { explanation: true, prompt: true, type: true, score: true, options: [true], correctOptionIndex: true, acceptedKeywords: [true] };
const taskFields = { title: true, description: true, instructions: true, maxScore: true, dueDays: true, formQuestions: [questionFields] };
const quizFields = { title: true, maxScore: true, passingScore: true, questions: [questionFields] };
const lessonFields = { studyMaterial: { title: true, content: true }, title: true, description: true, durationMinutes: true, tasks: [taskFields], quiz: quizFields };
const certificateFields = Object.fromEntries(['companyName', 'cnpj', 'address', 'contact', 'signerName', 'primaryColor', 'logoPosition', 'bodyTextTemplate', 'templateId', 'fontFamily', 'backgroundColor', 'accentColor', 'heading', 'subtitle', 'signerTitle'].map(key => [key, true]));
const courseFields = { title: true, description: true, estimatedHours: true, difficulty: true, activityNotificationEmail: true, certificateConfig: certificateFields, modules: [{ title: true, description: true, lessons: [lessonFields] }] };

export function getCourseAITargets(draft) {
  const targets = [];
  const visit = (value, path, shape) => {
    if (path) targets.push({ value: path, label: labelPath(path) });
    if (!value || typeof value !== 'object' || shape === true) return;
    if (Array.isArray(shape)) {
      if (Array.isArray(value)) value.forEach((child, index) => visit(child, `${path}.${index}`, shape[0]));
      return;
    }
    Object.entries(value).forEach(([key, child]) => {
      if (Object.prototype.hasOwnProperty.call(shape, key)) visit(child, path ? `${path}.${key}` : key, shape[key]);
    });
  };
  visit(draft, '', courseFields);
  return targets;
}

const panel = 'mb-4 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 sm:p-5';
const inputStyle = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800';
const primaryButton = 'min-h-10 rounded-xl bg-indigo-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50';
const secondaryButton = 'min-h-10 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50';
const copy = value => JSON.parse(JSON.stringify(value));
const failureMessage = (failure, fallback) => {
  const message = failure?.response?.data?.error;
  return typeof message === 'string' && message.trim() && message.length <= 600 ? message : fallback;
};
const displayValue = value => value === undefined || value === null || value === '' ? '(vazio)' : typeof value === 'boolean' ? (value ? 'Sim' : 'Não') : String(value);
const changesBetween = (before, after, path = '') => {
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  if ((before && typeof before === 'object') || (after && typeof after === 'object')) {
    const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    return [...keys].flatMap(key => protectedField(key) ? [] : changesBetween(before?.[key], after?.[key], path ? `${path}.${key}` : key));
  }
  return path === 'coverImageUrl' ? [] : [{ path, before, after }];
};

function CourseAIPreview({ before, after }) {
  const changes = changesBetween(before, after);
  return <div className="mt-4 rounded-xl border border-indigo-200 bg-white p-4" aria-label="Prévia da Nash IA">
    <h4 className="font-bold text-slate-900">Confira a sugestão antes de aplicar</h4>
    {after.coverImageUrl && after.coverImageUrl !== before.coverImageUrl && <img src={after.coverImageUrl} alt="Capa sugerida pela Nash IA" className="my-3 max-h-64 max-w-full rounded-xl object-contain" />}
    <div className="mt-3 max-h-96 space-y-3 overflow-y-auto">
      {changes.map(change => <div key={change.path} className="rounded-lg bg-slate-50 p-3 text-sm">
        <p className="font-semibold text-slate-800">{labelPath(change.path)}</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-slate-500"><span className="font-semibold">Atual: </span>{displayValue(change.before)}</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-slate-800"><span className="font-semibold">Sugestão: </span>{displayValue(change.after)}</p>
      </div>)}
    </div>
    {!changes.length && after.coverImageUrl === before.coverImageUrl && <p className="mt-2 text-sm text-slate-600">A sugestão não altera os campos do rascunho.</p>}
  </div>;
}

export function CourseAIEditor({ draft, onApply, contextKey = 'new', disabled = false }) {
  const [prompt, setPrompt] = useState('');
  const [target, setTarget] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [structureOnly, setStructureOnly] = useState(false);
  const [work, setWork] = useState(null);
  const [progress, setProgress] = useState('');
  const [preview, setPreview] = useState(null);
  const [undo, setUndo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  const currentRef = useRef(null);
  const serialized = JSON.stringify(draft);
  const identity = `${contextKey}:${serialized}`;
  currentRef.current = { identity, disabled };
  const targets = useMemo(() => getCourseAITargets(draft), [draft]);

  useEffect(() => {
    requestRef.current += 1;
    setPreview(null); setWork(null); setProgress(''); setBusy(false); setError('');
    setUndo(previous => previous?.applied === identity ? previous : null);
  }, [identity, disabled]);
  useEffect(() => () => { requestRef.current += 1; }, []);

  const invalidate = () => { requestRef.current += 1; setPreview(null); setWork(null); setProgress(''); setBusy(false); setError(''); };
  const generate = async (cover = false, resume = null) => {
    if (!prompt.trim() || disabled || busy) return;
    const requestId = ++requestRef.current;
    const originalIdentity = identity;
    const baseline = resume?.before || copy(draft);
    const isCurrent = () => requestId === requestRef.current && currentRef.current.identity === originalIdentity && !currentRef.current.disabled;
    setBusy(true); setPreview(null); setError(''); setProgress('');
    if (!resume) setWork(null);
    try {
      const scopedTarget = target && targets.some(item => item.value === target) ? target : '';
      const complete = !cover && !scopedTarget && !structureOnly;
      let suggested;
      if (resume) suggested = resume.after;
      else {
        const response = cover
          ? await CourseService.generateAICover({ prompt: prompt.trim() })
          : await CourseService.authorWithAI({ prompt: prompt.trim(), draft: baseline, ...(scopedTarget ? { target: scopedTarget } : {}), ...(complete ? { mode: 'plan' } : {}) });
        if (!isCurrent()) return;
        suggested = cover ? { ...baseline, coverImageUrl: response.coverImageUrl } : response.draft;
        if (cover && !response.coverImageUrl) throw new Error('invalid cover');
      }
      if (!suggested || typeof suggested !== 'object' || !Array.isArray(suggested.modules)) throw new Error('invalid response');
      if (complete) {
        const positions = resume?.positions || suggested.modules.flatMap((module, moduleIndex) => (module.lessons || []).map((lesson, lessonIndex) => ({ moduleIndex, lessonIndex })));
        if (!positions.length) throw new Error('empty course');
        let pending = resume || { before: baseline, after: suggested, positions, next: 0, identity: originalIdentity, prompt: prompt.trim() };
        setWork(pending);
        for (let index = pending.next; index < positions.length; index += 1) {
          if (!isCurrent()) return;
          const { moduleIndex, lessonIndex } = positions[index];
          setProgress(`Aula ${index + 1}/${positions.length} · ${suggested.modules[moduleIndex].lessons[lessonIndex].title || 'Preparando aula'}`);
          const response = await CourseService.elaborateLessonWithAI({ prompt: pending.prompt, draft: suggested, moduleIndex, lessonIndex });
          if (!isCurrent()) return;
          if (!response?.lesson || typeof response.lesson !== 'object' || Array.isArray(response.lesson)) throw new Error('invalid lesson');
          suggested = copy(suggested);
          suggested.modules[moduleIndex].lessons[lessonIndex] = response.lesson;
          pending = { ...pending, after: suggested, next: index + 1 };
          setWork(pending);
        }
      }
      if (!isCurrent()) return;
      setWork(null); setProgress('');
      setPreview({ before: baseline, after: suggested, identity: originalIdentity });
    } catch (failure) {
      if (isCurrent()) setError(failureMessage(failure, 'Não foi possível gerar a sugestão da Nash IA. Tente novamente.'));
    } finally { if (requestId === requestRef.current) setBusy(false); }
  };
  const applicable = preview?.identity === identity && !disabled;
  return <section className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4" aria-label="Autoria de cursos com Nash IA">
    <h3 className="font-bold text-indigo-950">Nash IA</h3>
    <label className="mt-3 block text-xs font-semibold text-slate-700">Pedido para a Nash IA
      <textarea className={`${inputStyle} mt-1 resize-y`} rows={2} value={prompt} disabled={disabled} placeholder="Descreva o curso: a Nash IA prepara aulas, apostilas e avaliações. Você revisa antes de aplicar." onChange={event => { invalidate(); setPrompt(event.target.value); }} />
    </label>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button type="button" className={primaryButton} disabled={disabled || busy || !!work || !prompt.trim()} onClick={() => generate()}>Gerar prévia do curso</button>
      <button type="button" className="min-h-10 rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100" aria-expanded={showOptions} onClick={() => setShowOptions(previous => !previous)}>Mais opções da Nash IA</button>
      {undo?.applied === identity && <button type="button" className={secondaryButton} disabled={disabled || busy} onClick={() => { invalidate(); onApply(copy(undo.before)); setUndo(null); }}>Desfazer aplicação da IA</button>}
    </div>
    {showOptions && <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-700"><input type="checkbox" checked={structureOnly} disabled={disabled} onChange={event => { invalidate(); setStructureOnly(event.target.checked); }} />Somente estrutura</label>
      <label className="block text-xs font-semibold text-slate-700">Parte do curso para alterar
        <select className={`${inputStyle} mt-1`} value={targets.some(item => item.value === target) ? target : ''} disabled={disabled} onChange={event => { invalidate(); setTarget(event.target.value); }}>
          <option value="">Curso completo</option>
          {targets.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
        </select>
      </label>
      <button type="button" className={secondaryButton} disabled={disabled || busy || !prompt.trim()} onClick={() => generate(true)}>Gerar prévia da capa</button>
    </div>}
    {!showOptions && target && targets.some(item => item.value === target) && <p className="mt-2 text-xs text-slate-600">Alterar: {labelPath(target)}</p>}
    {busy && <div className="mt-3"><p role="status" className="text-sm text-indigo-800">{progress || 'A Nash IA está preparando a prévia…'}</p><button type="button" className={`${secondaryButton} mt-2`} onClick={invalidate}>Cancelar geração</button></div>}
    {work && !busy && work.identity === identity && <div className="mt-3 flex flex-wrap gap-2">
      <p className="w-full text-xs text-slate-600">As aulas concluídas estão preservadas nesta prévia. Continue para finalizar o curso.</p>
      <button type="button" className={primaryButton} disabled={disabled} onClick={() => generate(false, work)}>Continuar geração</button>
      <button type="button" className={secondaryButton} onClick={invalidate}>Descartar geração</button>
    </div>}
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    {applicable && <>
      <CourseAIPreview before={preview.before} after={preview.after} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={primaryButton} onClick={() => {
          if (currentRef.current.identity !== preview.identity || currentRef.current.disabled) return;
          const next = copy(preview.after);
          setUndo({ before: preview.before, applied: `${contextKey}:${JSON.stringify(next)}` });
          setPreview(null); onApply(next);
        }}>Aplicar ao rascunho</button>
        <button type="button" className={secondaryButton} onClick={() => setPreview(null)}>Descartar prévia</button>
      </div>
    </>}
  </section>;
}

export function CourseAIAdvisor({ courses = [], onOpenCourse }) {
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  useEffect(() => () => { requestRef.current += 1; }, []);
  const recommend = async () => {
    if (!prompt.trim() || busy) return;
    const requestId = ++requestRef.current;
    setBusy(true); setError(''); setResult(null);
    try {
      const response = await CourseService.recommendWithAI({ prompt: prompt.trim() });
      if (requestId === requestRef.current) setResult(response);
    } catch (failure) {
      if (requestId === requestRef.current) setError(failureMessage(failure, 'Não foi possível consultar a Nash IA. Tente novamente.'));
    } finally { if (requestId === requestRef.current) setBusy(false); }
  };
  const recommendations = (result?.recommendations || []).filter(item => courses.some(course => String(course.id) === String(item.courseId)));
  return <section className={panel} aria-label="Orientação de cursos com Nash IA">
    <h3 className="font-bold text-indigo-950">Nash IA · Encontre seu próximo curso</h3>
    <p className="mt-1 text-sm text-slate-600">Conte seu objetivo e receba sugestões dos cursos disponíveis para você.</p>
    <label className="mt-3 block text-xs font-semibold text-slate-700">O que você quer aprender?
      <textarea className={`${inputStyle} mt-1 min-h-20`} value={prompt} placeholder="Ex.: Quero me preparar para meu primeiro emprego." onChange={event => { requestRef.current += 1; setPrompt(event.target.value); setResult(null); setBusy(false); setError(''); }} />
    </label>
    <button type="button" className={`${primaryButton} mt-3`} disabled={busy || !prompt.trim()} onClick={recommend}>Pedir orientação à Nash IA</button>
    {busy && <p role="status" className="mt-3 text-sm text-indigo-800">Buscando cursos para o seu objetivo…</p>}
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    {result && <div className="mt-4 space-y-3" aria-live="polite">
      <p className="whitespace-pre-wrap text-sm text-slate-700">{result.summary}</p>
      {recommendations.map((item, index) => {
        const course = courses.find(entry => String(entry.id) === String(item.courseId));
        return <div className="rounded-xl border border-indigo-100 bg-white p-3" key={`${item.courseId}-${index}`}>
          <h4 className="text-sm font-bold text-slate-900">{course.title}</h4>
          <p className="my-2 text-sm text-slate-600">{item.reason}</p>
          <button type="button" className={secondaryButton} aria-label={`Abrir curso ${course.title}`} onClick={() => onOpenCourse(course.id)}>Ver curso</button>
        </div>;
      })}
      {!recommendations.length && <p className="text-sm text-slate-600">Nenhuma recomendação disponível para este objetivo. Tente detalhar seu interesse.</p>}
    </div>}
  </section>;
}
